import { useSyncExternalStore } from "react";

import { logger } from "@/lib/logger";

/**
 * Where the sidebar sits, by the window's width, and whether the sidebar
 * beside the page is a narrow rail of icons or shows its labels.
 *
 * - Below 640 px (a phone) there is no sidebar column: a top bar's menu
 *   button opens the full sidebar in a sheet.
 * - From 640 px to 1023 px (a tablet, a laptop at half screen) the rail of
 *   icons is always there. Its button opens the full sidebar as a panel over
 *   the page, which neither moves nor squeezes the page. That panel is not
 *   remembered: a narrow window always starts as the rail.
 * - From 1024 px up the sidebar sits beside the page, full or collapsed to
 *   the rail. A person's own choice is remembered in this browser; until they
 *   make one, it starts collapsed below 1280 px wide and expanded from there.
 *
 * The widths are px, never rem: the root font size here is 18 px, while a
 * media query's rem stays the browser's 16 px, so a rem width is easy to
 * misread.
 */

/** Where the sidebar sits: the phone's sheet, the rail over the page, or beside it. */
export type SidebarLayout = "phone" | "rail" | "docked";

/** What a person chose with the sidebar's button beside the page. */
export type SidebarChoice = "collapsed" | "expanded";

/** From this width up the sidebar has a column of its own; below it, the phone's top bar. */
export const RAIL_MIN_WIDTH = 640;

/** From this width up the sidebar sits beside the page; below it, the rail opens over the page. */
export const DOCKED_MIN_WIDTH = 1024;

/** From this width up the sidebar beside the page starts expanded, until a choice. */
export const WIDE_MIN_WIDTH = 1280;

const STORAGE_KEY = "dc-sidebar";

/** A media query that matches a window at least `px` wide. */
export function minWidthQuery(px: number): string {
  return `(min-width: ${px}px)`;
}

/**
 * The sidebar's layout, from whether the window is at least a given width
 * wide: a media query in the browser, a plain comparison in a test.
 */
export function sidebarLayout(atLeast: (px: number) => boolean): SidebarLayout {
  if (!atLeast(RAIL_MIN_WIDTH)) {
    return "phone";
  }
  return atLeast(DOCKED_MIN_WIDTH) ? "docked" : "rail";
}

export function isSidebarChoice(value: unknown): value is SidebarChoice {
  return value === "collapsed" || value === "expanded";
}

/** Collapsed or not beside the page: the person's choice when they made one, else by the window's width. */
export function isSidebarCollapsed(choice: SidebarChoice | null, wide: boolean): boolean {
  return choice === null ? !wide : choice === "collapsed";
}

function matchesMinWidth(px: number): boolean {
  return window.matchMedia(minWidthQuery(px)).matches;
}

/** Calls `onChange` whenever the window crosses one of `widths`; returns the unsubscribe. */
function onWidthCrossing(widths: number[], onChange: () => void): () => void {
  const queries = widths.map((px) => window.matchMedia(minWidthQuery(px)));
  for (const query of queries) {
    query.addEventListener("change", onChange);
  }
  return () => {
    for (const query of queries) {
      query.removeEventListener("change", onChange);
    }
  };
}

function subscribeLayout(onChange: () => void): () => void {
  return onWidthCrossing([RAIL_MIN_WIDTH, DOCKED_MIN_WIDTH], onChange);
}

function getLayoutSnapshot(): SidebarLayout {
  return sidebarLayout(matchesMinWidth);
}

/**
 * Where the sidebar sits in this window; re-renders as the window crosses
 * 640 or 1024 px. The app shell renders either the sidebar's column or the
 * phone's top bar and sheet from it, so only one copy of the navigation (and
 * its account menu) is ever mounted.
 */
export function useSidebarLayout(): SidebarLayout {
  return useSyncExternalStore(subscribeLayout, getLayoutSnapshot, () => "docked");
}

/** The choice this browser remembers, or null; null too when storage is blocked. */
function readChoice(): SidebarChoice | null {
  try {
    const stored = globalThis.localStorage?.getItem(STORAGE_KEY);
    return isSidebarChoice(stored) ? stored : null;
  } catch {
    // Storage can be blocked (private mode, site data off): the width decides.
    return null;
  }
}

let choice: SidebarChoice | null = readChoice();
const listeners = new Set<() => void>();

/** Remembers a choice in this browser and applies it at once. */
export function setSidebarChoice(next: SidebarChoice): void {
  choice = next;
  try {
    globalThis.localStorage?.setItem(STORAGE_KEY, next);
  } catch {
    // Not remembered, but the choice still holds for this visit.
  }
  logger.debug("[nav]", "sidebar", next);
  for (const listener of listeners) {
    listener();
  }
}

function subscribeCollapsed(onChange: () => void): () => void {
  listeners.add(onChange);
  const stopWatchingWidth = onWidthCrossing([WIDE_MIN_WIDTH], onChange);
  // Another tab of the dashboard made a choice: follow it.
  const onStorage = (event: StorageEvent) => {
    if (event.key === STORAGE_KEY) {
      choice = readChoice();
      onChange();
    }
  };
  window.addEventListener("storage", onStorage);
  return () => {
    listeners.delete(onChange);
    stopWatchingWidth();
    window.removeEventListener("storage", onStorage);
  };
}

function getCollapsedSnapshot(): boolean {
  return isSidebarCollapsed(choice, matchesMinWidth(WIDE_MIN_WIDTH));
}

/**
 * Whether the sidebar beside the page is collapsed; re-renders on a choice and
 * as the window crosses 1280 px. The rail over the page (below 1024 px) does
 * not read it: its panel is open only until the next click outside.
 */
export function useSidebarCollapsed(): boolean {
  return useSyncExternalStore(subscribeCollapsed, getCollapsedSnapshot, () => false);
}
